import { useCallback, useEffect, useState } from 'react';

import { bffQuery } from '../lib/bffClient';
import { creditLevel as frameworkCreditLevel } from '@parsina/framework/tokens';

/**
 * اعتبار پارسینا («پارسه») — the SIGNED-IN PERSON's balance and the price list.
 *
 * ── WHOSE BALANCE ───────────────────────────────────────────────────────────
 * Your own. Every Parsina wallet belongs to one person
 * (docs/wallet-person-billing/CONTRACTS.md §0), and whoever performs a billable
 * action pays for it: the receptionist who dictates a walk-in, the staff member
 * who sends a WhatsApp. The clinic has no shared pool any more, so there is ONE
 * balance on this screen, and every surface says «اعتبار شما».
 *
 * `balance` is what a new charge can take — the wallet's `available`, which
 * already subtracts holds for sends still in flight — because that is the number
 * a «کافی است؟» check has to use. It is null, never 0, when the wallet could not
 * be read.
 *
 * The BFF resolves the owner from the verified token, so this hook sends no owner
 * and cannot ask about anyone else's.
 *
 * ── WHY THE PRICES COME FROM THE SERVER ─────────────────────────────────────
 * `clinicCreditPrices` mirrors the schedule that actually debits. A price
 * hardcoded here would eventually disagree with the ledger. That is why `cost`
 * is null until the query answers, and why the caller says «نامشخص» rather than
 * «۰» in the meantime.
 */

const CREDITS_QUERY = `
  query MyCredits {
    myPersonalCreditBalance {
      balance
      available
      currency
      lastUpdated
    }
    clinicCreditPrices {
      action
      cost
      per
    }
  }
`;

/**
 * The framework's level, with this panel's fourth state kept.
 *
 * The kit answers `null` for a balance it does not know; this panel names that
 * state «نامشخص» instead, because its callers switch on a string.
 */
export function creditLevel(balance) {
  return frameworkCreditLevel(balance) ?? 'unknown';
}

export function useCredits() {
  const [balance, setBalance] = useState(null);
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    return bffQuery(CREDITS_QUERY)
      .then((data) => {
        // null, never 0, when the answer did not arrive: a zero balance and an
        // unknown one look identical on screen and mean opposite things — one
        // says «شارژ کنید», the other says «دوباره تلاش کنید».
        const mine = data?.myPersonalCreditBalance;
        setBalance(mine ? (mine.available ?? mine.balance ?? null) : null);
        setPrices(data?.clinicCreditPrices ?? null);
      })
      .catch(() => {
        setBalance(null);
        setPrices(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Come back to the tab and the balance may have moved — the same wallet is
  // spent from every Parsina panel this person uses.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  /** What one action costs, or null while unknown. */
  const priceOf = useCallback(
    (action) => prices?.find((p) => p.action === action)?.cost ?? null,
    [prices],
  );

  return {
    balance, prices, priceOf, loading, level: creditLevel(balance), refresh: load,
  };
}

export default useCredits;
